import React, { useState, useMemo, useEffect } from 'react'
import { Form, Alert } from 'react-bootstrap'
import { updateDocument } from '../../services/documentsApi'
import { CompanySearchModal } from '../Samples/CompanySearchModal'
import { ContactCompanySearchResult } from '../../models/companies'
import { Modal } from '../UI/Modal/Modal'
import { CreatableTagSelector } from '../CreatableTagSelector'
import { useForm, FormProvider } from 'react-hook-form'
import apiService from '../../services/api'
import { ProductSearchModal } from '../UI/ProductSearchModal'
import type { ProductVariantSelection } from '../UI/ProductSearchModal/types'
import { Select as UISelect } from '../UI/Select/Select'
import type { ISelectItem } from '../UI/Select/Select'
import { hasProductNoVariants } from '../../utils/productHelpers'
import { Plus, X } from 'lucide-react'
import type { Document } from '../../types/documents'
import { BrandSelector } from '../UI/BrandSelector'
import { featureFlagService } from '../../utils/featureFlagService'

interface EditDocumentModalProps {
  document: Document
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface FormValues {
  name: string
  tags: string[]
}

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  document,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { enableBrandManagement } = featureFlagService()
  const api = apiService()

  // Fetch products and document tags
  const { data: products, isLoading: isLoadingProducts } = api.useGetProducts()
  const { data: documentTags } = api.useGetTags('documents')

  // Convert to options format for CreatableTagSelector
  const tagsOptions = useMemo(
    () =>
      documentTags?.map((tag) => ({
        label: tag.tag,
        value: tag.tag,
      })) || [],
    [documentTags]
  )

  // Form state with react-hook-form
  const methods = useForm<FormValues>({
    defaultValues: {
      name: document.name,
      tags: document.tags.map((tag) => tag.tag),
    },
  })

  // State
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    document.product_id?.toString() || null
  )
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    document.variant_id || null
  )
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(
    document.variant_name || null
  )
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [selectedCompany, setSelectedCompany] =
    useState<ContactCompanySearchResult | null>(
      document.contact_company_id
        ? {
            contact_company_id: document.contact_company_id,
            name: document.contact_company_name || '',
            domain: '',
          }
        : null
    )
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(
    document.brand_id ?? null
  )
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCompanySearch, setShowCompanySearch] = useState(false)

  // Reset form when document changes
  useEffect(() => {
    methods.reset({
      name: document.name,
      tags: document.tags.map((tag) => tag.tag),
    })
    setSelectedProductId(document.product_id?.toString() || null)
    setSelectedVariantId(document.variant_id || null)
    setSelectedVariantName(document.variant_name || null)
    setSelectedCompany(
      document.contact_company_id
        ? {
            contact_company_id: document.contact_company_id,
            name: document.contact_company_name || '',
            domain: '',
          }
        : null
    )
    setSelectedBrandId(document.brand_id ?? null)
    setError(null)
  }, [document, methods])

  // Reset form
  const handleClose = () => {
    methods.reset()
    setError(null)
    onClose()
  }

  // Update handler
  const handleUpdate = async () => {
    setIsUpdating(true)
    setError(null)

    try {
      const formValues = methods.getValues()

      // Build update data - only include changed fields
      const updateData: {
        name?: string
        tags?: string[]
        product_id?: number | null
        variant_id?: number | null
        contact_company_id?: number | null
        brand_id?: number | null
      } = {}

      // Always include name if changed
      if (formValues.name !== document.name) {
        updateData.name = formValues.name
      }

      // Always include tags (full replacement)
      const currentTags = document.tags.map((tag) => tag.tag).sort()
      const newTags = formValues.tags.sort()
      if (JSON.stringify(currentTags) !== JSON.stringify(newTags)) {
        updateData.tags = formValues.tags
      }

      // Product/Variant handling
      // Rule: If variant is selected, only send variant_id (product_id must be null)
      // If only product is selected (no variant), send product_id (variant_id must be null)
      const newVariantId = selectedVariantId
      const newProductId = newVariantId
        ? null // If variant selected, product_id must be null
        : selectedProductId
          ? Number(selectedProductId)
          : null

      // Check if product changed
      if (newProductId !== document.product_id) {
        updateData.product_id = newProductId
      }

      // Check if variant changed
      if (newVariantId !== document.variant_id) {
        updateData.variant_id = newVariantId
      }

      // Company handling
      const newCompanyId = selectedCompany?.contact_company_id || null
      if (newCompanyId !== document.contact_company_id) {
        updateData.contact_company_id = newCompanyId
      }

      // Brand handling
      const newBrandId = selectedBrandId
      if (newBrandId !== (document.brand_id ?? null)) {
        updateData.brand_id = newBrandId
      }

      // Only call API if there are changes
      if (Object.keys(updateData).length > 0) {
        await updateDocument(document.id, updateData)
        onSuccess?.()
      }

      handleClose()
    } catch (err: any) {
      setError(err.message || 'Update failed')
    } finally {
      setIsUpdating(false)
    }
  }

  // Convert products to dropdown items
  const productItems: ISelectItem[] = useMemo(
    () =>
      products?.map((p) => ({
        key: p.id.toString(),
        label: p.name,
        value: p.id.toString(),
      })) || [],
    [products]
  )

  // Get selected product
  const selectedProduct = useMemo(
    () => products?.find((p) => p.id.toString() === selectedProductId),
    [products, selectedProductId]
  )

  // Check if product has no variants
  const productHasNoVariants = hasProductNoVariants(products, selectedProductId)

  // Handle variant selection from modal
  const handleVariantSelect = (selection: ProductVariantSelection) => {
    if (selection.variants && selection.variants.length > 0) {
      const variant = selection.variants[0]
      setSelectedVariantId(variant.id)
      setSelectedVariantName(variant.name)
    }
    setShowVariantModal(false)
  }

  // Handle product selection
  const handleProductSelect = (productId: string | null) => {
    setSelectedProductId(productId)
    // Clear variant when product changes
    setSelectedVariantId(null)
    setSelectedVariantName(null)
  }

  // Clear product and variant
  const handleClearProduct = () => {
    setSelectedProductId(null)
    setSelectedVariantId(null)
    setSelectedVariantName(null)
  }

  return (
    <>
      <FormProvider {...methods}>
        <Modal
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleClose()
            }
          }}
          size="lg"
          title="Edit Document"
          onAccept={handleUpdate}
          acceptButtonText="Save Changes"
          blockAccept={isUpdating}
          loading={isUpdating}
          onCancel={handleClose}
          cancelButtonText="Cancel"
        >
          <div className="space-y-4">
            {/* Name Field */}
            <Form.Group>
              <Form.Label className="text-base font-medium text-gray-800 select-none mb-1">
                Document Name
              </Form.Label>
              <Form.Control type="text" {...methods.register('name')} />
            </Form.Group>

            {/* Tag Selector */}
            <CreatableTagSelector
              name="tags"
              control={methods.control}
              placeholder="Tags"
              options={tagsOptions}
              setError={methods.setError}
              tagParent="documents"
              label="Tags"
              optional
            />

            {/* Product & Variant Selector */}
            <div className="mt-4 space-y-3">
              <h3 className="text-base font-medium text-gray-800 select-none mb-1">
                Product & Variant
                <span className="text-sm italic font-normal"> - optional</span>
              </h3>

              {/* Product Dropdown - Hide when product is selected */}
              {!selectedProductId && (
                <div>
                  <UISelect
                    items={productItems}
                    selected={selectedProductId}
                    onSelect={handleProductSelect}
                    nullSelectionLabel="No product selected"
                    buttonVariant="secondary"
                    buttonClassName="w-full"
                    searchPlaceholder="Search products..."
                    disabled={isLoadingProducts}
                    matchTriggerWidth
                  />
                </div>
              )}

              {/* Show selection & variant button if product is selected */}
              {selectedProductId && selectedProduct && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {selectedProduct.name}
                      </div>
                      {selectedVariantName && (
                        <div className="text-sm text-gray-600 mt-1">
                          Variant: {selectedVariantName}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear product"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {/* Show variant button only if product has variants */}
                  {!productHasNoVariants && (
                    <button
                      type="button"
                      onClick={() => setShowVariantModal(true)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-[#2C6B4E] bg-[#EEF5F2] hover:bg-[#E5F2EA] transition-colors"
                    >
                      <Plus size={16} className="text-[#06512F]" />
                      <span className="font-medium text-sm leading-none text-[#06512F]">
                        {selectedVariantId
                          ? 'Change Variant'
                          : 'Select Variant (Optional)'}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Company Search */}
            <div className="mt-4">
              <h3 className="text-base font-medium text-gray-800 select-none mb-1">
                Company
                <span className="text-sm italic font-normal"> - optional</span>
              </h3>
              {selectedCompany ? (
                <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-white">
                  <span className="text-gray-900">{selectedCompany.name}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedCompany(null)}
                    className="text-sm text-new-company-green-active hover:text-new-company-green-dark hover:underline"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCompanySearch(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-[#2C6B4E] bg-[#EEF5F2] hover:bg-[#E5F2EA] transition-colors"
                >
                  <Plus size={16} className="text-[#06512F]" />
                  <span className="font-medium text-sm leading-none text-[#06512F]">
                    Select Company
                  </span>
                </button>
              )}
            </div>

            {/* Brand Selector */}
            {enableBrandManagement && (
              <div className="mt-4">
                <h3 className="text-base font-medium text-gray-800 select-none mb-1">
                  Brand
                  <span className="text-sm italic font-normal">
                    {' '}
                    - optional
                  </span>
                </h3>
                <BrandSelector
                  value={selectedBrandId}
                  onChange={setSelectedBrandId}
                  placeholder="Select a brand"
                />
              </div>
            )}

            {/* Error Display */}
            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        </Modal>

        {/* Company Search Modal */}
        <CompanySearchModal
          open={showCompanySearch}
          onClose={() => setShowCompanySearch(false)}
          onSelectCompany={(company) => {
            setSelectedCompany(company)
            setShowCompanySearch(false)
          }}
        />

        {/* Variant Search Modal */}
        <ProductSearchModal
          show={showVariantModal}
          onHide={() => setShowVariantModal(false)}
          selectionMode="variants-only"
          onSelect={handleVariantSelect}
          title="Select Variant"
          showProductFilter={false}
          multiSelect={false}
          maxSelections={1}
          restrictToProduct={
            selectedProductId ? Number(selectedProductId) : undefined
          }
        />
      </FormProvider>
    </>
  )
}
